import { DEFAULT_LOCALE, type Locale } from "./constants.js";
import { getWarningFixKey, getWarningProblemKey } from "./warningKeys.js";

import enCA from "./locales/en-CA/warnings.json" with { type: "json" };
import enUS from "./locales/en-US/warnings.json" with { type: "json" };
import frCA from "./locales/fr-CA/warnings.json" with { type: "json" };
import esMX from "./locales/es-MX/warnings.json" with { type: "json" };
import jaJP from "./locales/ja-JP/warnings.json" with { type: "json" };
import ptPT from "./locales/pt-PT/warnings.json" with { type: "json" };
import paIN from "./locales/pa-IN/warnings.json" with { type: "json" };

// Per-warning-type entry: a `title`, a `description` and a `fires-when` line (prospective
// copy describing what would trigger the type, carried for every type), optional
// per-variant nested entries (e.g. `{ "manual-open": { "description": "..." } }`), an
// optional `problems` map from problemKey to its translation template, and an optional
// `fixes` map from fixKey to its translation template.
export type WarningCatalogEntry = {
    title?: string;
    description?: string;
    "fires-when"?: string;
    problems?: Record<string, string>;
    fixes?: Record<string, string>;
} & {
    [variantKey: string]: { description?: string } | Record<string, string> | string | undefined;
};

export type WarningsCatalog = Record<string, WarningCatalogEntry>;

// Per-locale catalogs. The dashboard's stored-string `defaultValue` covers any key
// that hasn't been translated for a given locale.
export const warningCatalogs: Record<Locale, WarningsCatalog> = {
    "en-CA": enCA as WarningsCatalog,
    "en-US": enUS as WarningsCatalog,
    "fr-CA": frCA as WarningsCatalog,
    "es-MX": esMX as WarningsCatalog,
    "ja-JP": jaJP as WarningsCatalog,
    "pt-PT": ptPT as WarningsCatalog,
    "pa-IN": paIN as WarningsCatalog,
};

/**
 * Resolve a dot-separated key (e.g. `offline.problems.dead-battery`) against a
 * warnings catalog object and return the leaf string when present.
 * Use this for validation/tooling paths that need direct catalog lookups without
 * creating an i18next instance.
 */
export function lookupWarningCatalogString(catalog: WarningsCatalog, key: string): string | undefined {
    let cursor: unknown = catalog;
    for (const segment of key.split(".")) {
        if (cursor === null || typeof cursor !== "object") return undefined;
        cursor = (cursor as Record<string, unknown>)[segment];
    }
    return typeof cursor === "string" ? cursor : undefined;
}

/**
 * Returns the default-locale (English) display text for a warning problem key.
 * Persisting this at save time gives the UI a human-readable fallback if a
 * catalog key is later removed or renamed.
 * Call with warning type + problem key when preparing persisted warning payloads.
 */
export function getDefaultWarningProblemDisplayText(warningType: string, problemKey: string): string {
    const key = getWarningProblemKey(warningType, problemKey);
    return lookupWarningCatalogString(warningCatalogs[DEFAULT_LOCALE], key) ?? problemKey;
}

/**
 * Returns the default-locale (English) display text for a warning fix key.
 * Persisting this at save time gives the UI a human-readable fallback if a
 * catalog key is later removed or renamed.
 * Call with warning type + fix key when preparing persisted warning payloads.
 */
export function getDefaultWarningFixDisplayText(warningType: string, fixKey: string): string {
    const key = getWarningFixKey(warningType, fixKey);
    return lookupWarningCatalogString(warningCatalogs[DEFAULT_LOCALE], key) ?? fixKey;
}
